/**
 * LES COUPLES ET LES ENSEMBLES DE RÉELS — capacité `CAP-10` (tranche T6 du
 * chantier « mathématiques de bout en bout ») : la solution d'un système, les
 * coordonnées d'un point, un intervalle, l'ensemble-solution d'une inéquation.
 *
 * Module PUR, partagé : le serveur compare (`correction.ts`), l'application
 * compose ce que l'élève saisit et l'affiche, la console le relit. Aucun
 * modèle, aucun calcul formel : un ensemble de réels dont les bornes sont des
 * NOMBRES se compare exactement, en rationnels.
 *
 * ─── Le couple ──────────────────────────────────────────────────────────────
 *
 * `(3 ; 2)`. Le séparateur est le POINT-VIRGULE : au Québec la virgule est
 * décimale, et « (3, 2) » se lit « trois virgule deux ». L'élève saisit deux
 * champs ; ce qui part est `(a ; b)`. Une virgule n'est lue comme séparateur
 * que suivie d'une espace (« (3, 2) »), jamais collée (« (3,2) »).
 *
 * ─── L'ensemble ─────────────────────────────────────────────────────────────
 *
 * Une RÉUNION d'intervalles, rangée et fusionnée : c'est la valeur. Trois
 * écritures la disent, et `lireEnsemble` dit laquelle il a lue :
 *
 *  - en INTERVALLES, à la française : `[2 ; 5[`, `]-∞ ; 3]`, `]-∞ ; 1[ ∪ [4 ; +∞[`,
 *    `∅`, `ℝ` ;
 *  - en INÉGALITÉS : `x > 3`, `3 < x`, `-2 ≤ x < 5`, `x < 1 ou x ≥ 4`, `x ≠ 2` ;
 *  - en MOTS : « aucune solution », « tous les réels » ;
 *  - en EXTENSION (tranche T42) : un ensemble FINI de solutions, `{-3 ; 7}`, et
 *    les égalités qui le disent, `x = -3 ou x = 7`. Un nombre seul est un
 *    intervalle réduit à un point, `[7 ; 7]` : il se range, se fusionne et se
 *    compare comme les autres — `{-3 ; 7}` et `x = 7 ou x = -3` sont égaux.
 *
 * Deux écritures du même ensemble sont ÉGALES (`memesEnsembles`) : `x > 3` et
 * `]3 ; +∞[`. Ce qui distingue les erreurs types — un crochet inversé, une
 * borne incluse à tort, le sens de l'inégalité — ce sont des ensembles
 * DIFFÉRENTS : une réponse fautive prévue se reconnaît donc par son ensemble.
 *
 * Un crochet FERMÉ sur l'infini (`[2 ; +∞]`) n'est pas un ensemble de réels
 * mal lu : il est lu, et signalé (`defaut: 'infini_ferme'`).
 */
/* ───────────────────────────── les rationnels ───────────────────────────── */
/** Un rationnel réduit, dénominateur positif. */
import { SEPARATION_NON_LUE, lireNombresFractionnaires } from './nombre-fractionnaire.js';
const pgcd = (a, b) => (b === 0 ? Math.abs(a) : pgcd(b, a % b));
function rationnel(n, d) {
    if (!Number.isSafeInteger(n) || !Number.isSafeInteger(d) || d === 0)
        return null;
    const g = pgcd(n, d) || 1;
    const s = d < 0 ? -1 : 1;
    return { n: (s * n) / g, d: (s * d) / g };
}
const compare = (a, b) => a.n * b.d - b.n * a.d;
/** Un entier ou un décimal (virgule ou point), signe compris. */
function decimal(s) {
    const m = /^([+-]?)(\d+)(?:[.,](\d+))?$/.exec(s);
    if (!m)
        return null;
    const chiffres = m[3] ?? '';
    if (m[2].length + chiffres.length > 15)
        return null;
    const r = rationnel(Number(`${m[2]}${chiffres}`), 10 ** chiffres.length);
    return r === null || m[1] !== '-' ? r : { n: -r.n, d: r.d };
}
/**
 * Nettoie une écriture : délimiteurs, espaces LaTeX, signes typographiques.
 * Exportée pour `ensembles-exacts.ts` (tranche T45), qui lit la même écriture.
 */
export function nettoyerEcritureDEnsemble(brut) {
    return nettoyer(brut);
}
function nettoyer(brut) {
    return ((brut ?? '')
        .normalize('NFC')
        .replace(/\\\(|\\\)|\$/g, '')
        .replace(/\\(?:left|right|displaystyle)(?![a-zA-Z])/g, '')
        .replace(/\\(?:quad|qquad)(?![a-zA-Z])|\\[,;:!> ]|~/g, ' ')
        .replace(/\\(?:text|mathrm|textrm)\s*\{([^{}]*)\}/g, ' $1 ')
        .replace(/\{,\}/g, ',')
        .replace(/\\[dt]frac/g, '\\frac')
        .replace(/[−–—]/g, '-')
        .replace(/\\infty(?![a-zA-Z])/g, '∞')
        .replace(/\\(?:leqslant|leq|le)(?![a-zA-Z])|<=/g, '≤')
        .replace(/\\(?:geqslant|geq|ge)(?![a-zA-Z])|>=/g, '≥')
        .replace(/\\(?:neq|ne)(?![a-zA-Z])/g, '≠')
        .replace(/\\lt(?![a-zA-Z])/g, '<')
        .replace(/\\gt(?![a-zA-Z])/g, '>')
        .replace(/\\cup(?![a-zA-Z])/g, '∪')
        .replace(/\\(?:varnothing|emptyset)(?![a-zA-Z])|\\\{\s*\\\}/g, '∅')
        /* Les accolades d'un ensemble en extension (tranche T42) : `\{-3 ; 7\}`. */
        .replace(/\\[{}]/g, (a) => a.slice(1))
        .replace(/\\mathbb\s*\{R\}|\\R(?![a-zA-Z])/g, 'ℝ')
        .replace(/\\lbrack(?![a-zA-Z])/g, '[')
        .replace(/\\rbrack(?![a-zA-Z])/g, ']')
        .replace(/\s+/g, ' ')
        .trim());
}
/**
 * Un NOMBRE écrit par un élève ou un auteur : entier, décimal, fraction
 * (`3/4`, `\frac{3}{4}`), signe en tête. `null` : ce n'est pas un nombre — une
 * lettre, une racine, une expression.
 */
export function lireRationnel(brut) {
    /* « 8 1/12 » est huit et un douzième, pas 81/12 : le nombre fractionnaire est
       reconnu AVANT que les espaces ne soient retirées (`nombre-fractionnaire.ts`).
       Une fraction qui n'est pas plus petite que 1 (« 3 4/2 ») n'est pas devinée. */
    const lu = lireNombresFractionnaires(nettoyer(brut));
    if (lu.includes(SEPARATION_NON_LUE))
        return null;
    let s = lu.replace(/\s/g, '');
    let signe = 1;
    while (s.startsWith('+') || s.startsWith('-')) {
        if (s[0] === '-')
            signe = -signe;
        s = s.slice(1);
    }
    while (s.startsWith('(') && s.endsWith(')'))
        s = s.slice(1, -1);
    const mixte = /^\\mixte\{(\d+)\}\{(\d+)\}\{(\d+)\}$/.exec(s);
    if (mixte) {
        const [entier, haut, bas] = [Number(mixte[1]), Number(mixte[2]), Number(mixte[3])];
        const somme = rationnel(entier * bas + haut, bas);
        return somme === null ? null : { n: signe * somme.n, d: somme.d };
    }
    const fraction = /^\\frac\{([^{}]+)\}\{([^{}]+)\}$/.exec(s) ?? /^([^/]+)\/([^/]+)$/.exec(s);
    let r;
    if (fraction) {
        const haut = decimal(fraction[1]);
        const bas = decimal(fraction[2]);
        r =
            haut === null || bas === null || bas.n === 0
                ? null
                : rationnel(haut.n * bas.d, haut.d * bas.n);
    }
    else {
        r = decimal(s);
    }
    return r === null ? null : { n: signe * r.n, d: r.d };
}
/** Un rationnel, écrit à la française : entier, décimal fini, sinon fraction. */
export function ecrireRationnel(r) {
    const moins = r.n < 0 ? '−' : '';
    const n = Math.abs(r.n);
    if (r.d === 1)
        return `${moins}${n}`;
    let reste = r.d;
    for (const p of [2, 5])
        while (reste % p === 0)
            reste /= p;
    if (reste === 1) {
        const valeur = n / r.d;
        const ecrit = String(valeur);
        if (!/e/i.test(ecrit) && ecrit.length <= 12)
            return `${moins}${ecrit.replace('.', ',')}`;
    }
    return `${moins}${n}/${r.d}`;
}
/* ───────────────────────────── le couple ───────────────────────────── */
/**
 * Les deux composantes d'un couple, telles qu'écrites (des écritures, pas des
 * valeurs : `\frac{1}{2}` reste `\frac{1}{2}`). `null` : pas un couple lisible.
 */
export function lireCouple(brut) {
    let s = nettoyer(brut);
    if (s.startsWith('(') && s.endsWith(')'))
        s = s.slice(1, -1).trim();
    let parts;
    if (s.includes(';'))
        parts = s.split(';');
    /* Une virgule ne sépare que suivie d'une espace : « 3, 2 », jamais « 3,2 ». */ else if (/,\s/.test(s))
        parts = s.split(/,\s/);
    else
        return null;
    if (parts.length !== 2)
        return null;
    const [x, y] = parts.map((p) => p.trim());
    return x === '' || y === '' ? null : [x, y];
}
/** Le couple tel qu'il part au serveur et tel qu'on l'écrit : `(3 ; 2)`. */
export function ecrireCouple(x, y) {
    return `(${x.trim()} ; ${y.trim()})`;
}
export const ECRITURES_ENSEMBLE = ['intervalle', 'inegalite', 'mot', 'extension'];
const TOUS = { inf: null, sup: null, infInclus: false, supInclus: false };
/** L'intervalle est-il vide ? (`]2 ; 2[`, `[5 ; 3]`) */
function vide(i) {
    if (i.inf === null || i.sup === null)
        return false;
    const c = compare(i.inf, i.sup);
    return c > 0 || (c === 0 && !(i.infInclus && i.supInclus));
}
/** Range et fusionne : la forme sous laquelle deux ensembles se comparent. */
function canoniser(intervalles) {
    const rangees = intervalles
        .filter((i) => !vide(i))
        .sort((a, b) => {
        if (a.inf === null || b.inf === null)
            return a.inf === b.inf ? 0 : a.inf === null ? -1 : 1;
        return compare(a.inf, b.inf) || Number(b.infInclus) - Number(a.infInclus);
    });
    const sortie = [];
    for (const i of rangees) {
        const dernier = sortie[sortie.length - 1];
        if (dernier === undefined) {
            sortie.push(i);
            continue;
        }
        /* Se touchent-ils ? Le suivant commence avant la fin du précédent, ou
           exactement à sa fin si l'un des deux la contient. */
        const c = dernier.sup === null || i.inf === null ? -1 : compare(i.inf, dernier.sup);
        if (c < 0 || (c === 0 && (i.infInclus || dernier.supInclus))) {
            const plusLoin = dernier.sup === null
                ? 0
                : i.sup === null
                    ? 1
                    : compare(i.sup, dernier.sup) || Number(i.supInclus) - Number(dernier.supInclus);
            if (plusLoin > 0) {
                sortie[sortie.length - 1] = { ...dernier, sup: i.sup, supInclus: i.supInclus };
            }
        }
        else
            sortie.push(i);
    }
    return sortie;
}
function intersection(a, b) {
    const sortie = [];
    for (const p of a) {
        for (const q of b) {
            const cInf = p.inf === null ? -1 : q.inf === null ? 1 : compare(p.inf, q.inf);
            const cSup = p.sup === null ? 1 : q.sup === null ? -1 : compare(p.sup, q.sup);
            sortie.push({
                inf: cInf >= 0 ? p.inf : q.inf,
                infInclus: cInf > 0 ? p.infInclus : cInf < 0 ? q.infInclus : p.infInclus && q.infInclus,
                sup: cSup <= 0 ? p.sup : q.sup,
                supInclus: cSup < 0 ? p.supInclus : cSup > 0 ? q.supInclus : p.supInclus && q.supInclus,
            });
        }
    }
    return canoniser(sortie);
}
const borne = (s) => {
    const t = s.replace(/\s/g, '');
    if (t === '-∞')
        return '-inf';
    if (t === '+∞' || t === '∞')
        return '+inf';
    return lireRationnel(t);
};
/** Un intervalle à la française : `[2 ; 5[`, `]-∞ ; 3]`. */
function lireIntervalle(s) {
    const m = /^([[\]])(.+)([[\]])$/.exec(s.trim());
    if (!m)
        return null;
    const dedans = m[2];
    let parts;
    if (dedans.includes(';'))
        parts = dedans.split(';');
    else if (/,\s/.test(dedans))
        parts = dedans.split(/,\s/);
    else if ((dedans.match(/,/g) ?? []).length === 1)
        parts = dedans.split(',');
    else
        return null;
    if (parts.length !== 2)
        return null;
    const a = borne(parts[0]);
    const b = borne(parts[1]);
    if (a === null || b === null || a === '+inf' || b === '-inf')
        return null;
    const infInclus = m[1] === '[';
    const supInclus = m[3] === ']';
    return {
        i: {
            inf: a === '-inf' ? null : a,
            sup: b === '+inf' ? null : b,
            infInclus: a === '-inf' ? false : infInclus,
            supInclus: b === '+inf' ? false : supInclus,
        },
        infiniFerme: (a === '-inf' && infInclus) || (b === '+inf' && supInclus),
    };
}
const RETOURNEE = {
    '<': '>',
    '>': '<',
    '≤': '≥',
    '≥': '≤',
    '≠': '≠',
    '=': '=',
};
/** `variable REL valeur`, en ensemble. */
function demiDroite(rel, v) {
    switch (rel) {
        case '<':
            return [{ ...TOUS, sup: v }];
        case '≤':
            return [{ ...TOUS, sup: v, supInclus: true }];
        case '>':
            return [{ ...TOUS, inf: v }];
        case '≥':
            return [{ ...TOUS, inf: v, infInclus: true }];
        case '≠':
            return [
                { ...TOUS, sup: v },
                { ...TOUS, inf: v },
            ];
        /* Tranche T42 : une égalité — un seul nombre, un intervalle réduit à un point. */
        case '=':
            return [{ inf: v, sup: v, infInclus: true, supInclus: true }];
    }
}
/**
 * Un ensemble FINI écrit en extension (tranche T42) : `{-3 ; 7}`, `{2}`. Le
 * séparateur est le point-virgule, comme pour un couple ; une virgule n'en est
 * un que suivie d'une espace. `null` : pas des accolades, ou un élément qui
 * n'est pas un nombre.
 */
function lireExtension(s) {
    const m = /^\{(.*)\}$/.exec(s.trim());
    if (!m)
        return null;
    const dedans = m[1].trim();
    if (dedans === '')
        return [];
    const elements = dedans.includes(';') ? dedans.split(';') : dedans.split(/,\s/);
    const points = [];
    for (const e of elements) {
        const v = lireRationnel(e.trim());
        if (v === null)
            return null;
        points.push({ inf: v, sup: v, infInclus: true, supInclus: true });
    }
    return canoniser(points);
}
/** L'intervalle est-il réduit à un point ? */
const unPoint = (i) => i.inf !== null && i.sup !== null && i.infInclus && i.supInclus && compare(i.inf, i.sup) === 0;
/** Une inégalité simple ou double sur `variable` : `x > 3`, `3 < x`, `-2 ≤ x < 5`. */
function lireInegalite(s, variable) {
    const morceaux = s.split(/([<>≤≥≠=])/).map((m) => m.trim());
    if (morceaux.length !== 3 && morceaux.length !== 5)
        return null;
    const membres = morceaux.filter((_, k) => k % 2 === 0);
    const relations = morceaux.filter((_, k) => k % 2 === 1);
    if (membres.some((m) => m === ''))
        return null;
    if (membres.length === 2) {
        const [g, d] = membres;
        const rel = relations[0];
        if (g === variable) {
            const v = lireRationnel(d);
            return v === null ? null : demiDroite(rel, v);
        }
        if (d === variable) {
            const v = lireRationnel(g);
            return v === null ? null : demiDroite(RETOURNEE[rel], v);
        }
        return null;
    }
    const [g, milieu, d] = membres;
    const [r1, r2] = relations;
    if (milieu !== variable || r1 === '≠' || r2 === '≠' || r1 === '=' || r2 === '=')
        return null;
    /* Une double inégalité va dans UN sens : « 2 < x > 5 » ne se lit pas. */
    if ((r1 === '<' || r1 === '≤') !== (r2 === '<' || r2 === '≤'))
        return null;
    const a = lireRationnel(g);
    const b = lireRationnel(d);
    if (a === null || b === null)
        return null;
    return intersection(demiDroite(RETOURNEE[r1], a), demiDroite(r2, b));
}
const MOT_VIDE = /^(?:aucune solution|pas de solution|aucun r[ée]el|ensemble vide|vide)\.?$/i;
const MOT_TOUS = /^(?:tous les (?:nombres )?r[ée]els|tout r[ée]el|tous les nombres)\.?$/i;
/**
 * Lit un ensemble de réels, quelle que soit son écriture. `variable` : la
 * lettre d'une inégalité (défaut : `x`). `null` : rien de lisible — une borne
 * qui n'est pas un nombre, deux écritures mêlées, une autre variable.
 */
export function lireEnsemble(brut, variable = 'x') {
    const s = nettoyer(brut);
    if (s === '')
        return null;
    if (MOT_VIDE.test(s))
        return { ensemble: [], ecriture: 'mot' };
    if (MOT_TOUS.test(s))
        return { ensemble: [TOUS], ecriture: 'mot' };
    /* En intervalles : des morceaux réunis par « ∪ » (ou le mot « union ») ; un
       morceau en accolades est un ensemble fini (tranche T42). */
    if (/^[[\]∅ℝ{]/.test(s) || s === 'R') {
        const morceaux = s.split(/∪|\bunion\b|\bU\b/).map((m) => m.trim());
        const lus = [];
        let infiniFerme = false;
        let enExtension = true;
        for (const m of morceaux) {
            if (m === '∅')
                continue;
            if (m === 'ℝ' || m === 'R') {
                lus.push(TOUS);
                enExtension = false;
                continue;
            }
            const fini = lireExtension(m);
            if (fini !== null) {
                lus.push(...fini);
                continue;
            }
            const lu = lireIntervalle(m);
            if (lu === null)
                return null;
            lus.push(lu.i);
            enExtension = false;
            infiniFerme ||= lu.infiniFerme;
        }
        return {
            ensemble: canoniser(lus),
            ecriture: enExtension && s.startsWith('{') ? 'extension' : 'intervalle',
            ...(infiniFerme ? { defaut: 'infini_ferme' } : {}),
        };
    }
    /* En inégalités : « ou » réunit, « et » croise. Une égalité (« x = 7 ») dit un nombre. */
    if (!/[<>≤≥≠=]/.test(s))
        return null;
    let ensemble = [];
    for (const terme of s.split(/\s+ou\s+/i)) {
        let croise = [TOUS];
        for (const facteur of terme.split(/\s+et\s+/i)) {
            const lu = lireInegalite(facteur.trim(), variable);
            if (lu === null)
                return null;
            croise = intersection(croise, lu);
        }
        ensemble = canoniser([...ensemble, ...croise]);
    }
    return { ensemble, ecriture: 'inegalite' };
}
const memeBorne = (a, b) => a === null || b === null ? a === b : compare(a, b) === 0;
/** Deux ensembles (déjà rangés et fusionnés par `lireEnsemble`) sont-ils le même ? */
export function memesEnsembles(a, b) {
    return (a.length === b.length &&
        a.every((p, k) => {
            const q = b[k];
            return (memeBorne(p.inf, q.inf) &&
                memeBorne(p.sup, q.sup) &&
                p.infInclus === q.infInclus &&
                p.supInclus === q.supInclus);
        }));
}
/** L'ensemble en intervalles, à la française : `]−∞ ; 1[ ∪ [4 ; +∞[`, `∅`, `ℝ`. */
export function ecrireEnIntervalles(e) {
    if (e.length === 0)
        return '∅';
    if (e.length === 1 && e[0]?.inf === null && e[0].sup === null)
        return 'ℝ';
    /* Un ensemble FINI s'écrit en extension (tranche T42) : `{−3 ; 7}`. */
    if (e.every(unPoint))
        return `{${e.map((i) => ecrireRationnel(i.inf)).join(' ; ')}}`;
    return e
        .map((i) => {
        if (unPoint(i))
            return `{${ecrireRationnel(i.inf)}}`;
        const g = i.inf === null ? ']−∞' : `${i.infInclus ? '[' : ']'}${ecrireRationnel(i.inf)}`;
        const d = i.sup === null ? '+∞[' : `${ecrireRationnel(i.sup)}${i.supInclus ? ']' : '['}`;
        return `${g} ; ${d}`;
    })
        .join(' ∪ ');
}
/** L'ensemble en inégalités : `x < 1 ou x ≥ 4`, `−2 ≤ x < 5`, « aucune solution », « tous les réels ». */
export function ecrireEnInegalites(e, variable = 'x') {
    if (e.length === 0)
        return 'aucune solution';
    if (e.length === 1 && e[0]?.inf === null && e[0].sup === null)
        return 'tous les réels';
    return e
        .map((i) => {
        if (unPoint(i))
            return `${variable} = ${ecrireRationnel(i.inf)}`;
        if (i.inf === null)
            return `${variable} ${i.supInclus ? '≤' : '<'} ${ecrireRationnel(i.sup)}`;
        if (i.sup === null)
            return `${variable} ${i.infInclus ? '≥' : '>'} ${ecrireRationnel(i.inf)}`;
        return `${ecrireRationnel(i.inf)} ${i.infInclus ? '≤' : '<'} ${variable} ${i.supInclus ? '≤' : '<'} ${ecrireRationnel(i.sup)}`;
    })
        .join(' ou ');
}
//# sourceMappingURL=ensembles.js.map